import { useAccountState } from '../redux/reducers/accountReducer';
import { useCaseService } from "../data/services";
import { User } from "../data/types";

export const useAccess = (user?: User) => {

    const caseService = useCaseService();
    const { primaryAccount } = useAccountState();

    const isMasterUser = (user?: User) => {
        return primaryAccount?.account.masterUsername == user?.username;
    }

    //Account
    const isAccountAdmin = () => {
        return !!user?.permissions?.adminPerms?.accountAdmin || isMasterUser(user);
    }
    const canCreateCases = () => {
        return !!isAccountAdmin()
            || !!user?.permissions?.adminPerms?.createCases;
    }
    const canEditCases = () => {
        return !!isAccountAdmin()
            || !!user?.permissions?.adminPerms?.editCases;
    }
    const canDeleteCases = () => {
        return !!isAccountAdmin()
            || !!user?.permissions?.adminPerms?.deleteCases;
    }
    const canViewAllCases = () => {
        return !!isAccountAdmin()
            || !!user?.permissions?.adminPerms?.createCases
            || !!user?.permissions?.adminPerms?.editCases
            || !!user?.permissions?.adminPerms?.deleteCases;
    }

    //Settings
    const canAccessAccountSettings = () => {
        return !!isAccountAdmin()
            || !!user?.permissions?.adminPerms?.addEditUsers
            || !!user?.permissions?.adminPerms?.deleteUsers
            || !!user?.permissions?.adminPerms?.changeAccountSettings
            || !!user?.permissions?.adminPerms?.editCaseDataTypes
            || !!user?.permissions?.adminPerms?.addEditParties
            || !!user?.permissions?.adminPerms?.deleteParties
    }
    const canAddEditUsers = () => {
        return !!isAccountAdmin() || !!user?.permissions?.adminPerms?.addEditUsers;
    }
    const canDeleteUsers = () => {
        return !!isAccountAdmin() || !!user?.permissions?.adminPerms?.deleteUsers;
    }
    const canChangeAccountSettings = () => {
        return !!isAccountAdmin() || !!user?.permissions?.adminPerms?.changeAccountSettings;
    }
    const canEditCaseDataTypes = () => {
        return !!isAccountAdmin() || !!user?.permissions?.adminPerms?.editCaseDataTypes;
    }
    const canAddEditParties = () => {
        return !!isAccountAdmin() || !!user?.permissions?.adminPerms?.addEditParties;
    }
    const canDeleteParties = () => {
        return !!isAccountAdmin() || !!user?.permissions?.adminPerms?.deleteParties;
    }


    //Cases
    const assignedToCase = (caseId?: string) => {
        return caseId ? caseService.isUserAssignedToCase(user?.userId, caseId) : false;
    }
    const isCaseLead = (caseId?: string) => {
        return assignedToCase(caseId) && user?.permissions?.casePerms?.caseLead;
    }

    const canEditCaseAsAdmin = (caseId?: string) => {
        return isAccountAdmin()
            || canEditCases()
            || isCaseLead(caseId);
    }

    const canEditCaseInfo = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.editCaseInfo);
    }
    const canEditCaseEngagement = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.editEngagement);
    }
    const canViewCaseEngagement = (caseId?: string) => {
        return canEditCaseEngagement(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.viewEngagement);
    }

    //Milestones
    const canCreateMilestones = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.createMilestones);
    }
    const canEditMilestones = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && (user?.permissions?.casePerms?.editMilestones || user?.permissions?.casePerms?.createMilestones));
    }
    const canDeleteMilestones = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.deleteMilestones);
    }
    const canCompleteMilestones = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.completeMilestones);
    }
    const canReorderMilestones = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.reorderMilestones);
    }
    const canReopenMilestones = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.reopenMilestones);
    }

    //Tasks
    const canViewAllTasks = () => {
        return user?.permissions?.casePerms?.viewAllTasks;
    }
    const canCreateTasks = () => {
        return user?.permissions?.casePerms?.createTasks;
    }
    const canCreateTasksForOthers = () => {
        return user?.permissions?.casePerms?.createTasksForOthers;
    }
    const canEditTasks = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.editTasks);
    }

    //Events
    const canAddEvents = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.addEvents);
    }
    const canEditEvents = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.editEvents);
    }
    const canDeleteEvents = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.deleteEvents);
    }

    //Notes
    const canAddNotes = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.addNotes);
    }

    //Documents
    const canViewDocuments = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.viewDocuments);
    }
    const canUploadDocuments = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.uploadDocuments);
    }
    const canDownloadDocuments = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.downloadDocuments);
    }
    const canEditDocuments = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.editDocuments);
    }
    const canDeleteDocuments = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.deleteDocuments);
    }

    //TimeTracking
    const canAddEditTrackedTime = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.addEditTrackedTime);
    }
    const canDeleteTrackedTime = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.deleteTrackedTime);
    }
    const canViewExpenses = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.viewExpenses);
    }
    const canAddExpenses = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.addExpenses);
    }
    const canDeleteOwnExpenses = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.deleteOwnExpenses);
    }
    const canViewBillableRates = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.viewBillableRates);
    }
    const canEditBillableRates = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.editBillableRates);
    }

    //Invoices
    const canViewInvoices = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.viewInvoices);
    }
    const canCreateInvoices = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.createInvoices);
    }
    const canDeleteInvoices = (caseId?: string) => {
        return canEditCaseAsAdmin(caseId)
            || (assignedToCase(caseId) && user?.permissions?.casePerms?.deleteInvoices);
    }

    //Templates
    const canSeeTemplates = () => {
        return canCreateEditEngagementTemplates()
            || canDeleteEngagementTemplates()
            || canCreateEditCaseTemplates()
            || canDeleteCaseTemplates();
    }
    const canSeeEngagementTemplates = () => {
        return canCreateEditEngagementTemplates()
            || canDeleteEngagementTemplates();
    }
    const canCreateEditEngagementTemplates = () => {
        return !!isAccountAdmin()
            || user?.permissions?.accountPerms?.createEditEngagementTemplates;
    }
    const canDeleteEngagementTemplates = () => {
        return !!isAccountAdmin()
            || user?.permissions?.accountPerms?.deleteEngagementTemplates;
    }
    const canSeeCaseTemplates = () => {
        return canCreateEditCaseTemplates()
            || canDeleteCaseTemplates();
    }
    const canCreateEditCaseTemplates = () => {
        return !!isAccountAdmin()
            || user?.permissions?.accountPerms?.createEditCaseTemplates;
    }
    const canDeleteCaseTemplates = () => {
        return !!isAccountAdmin()
            || user?.permissions?.accountPerms?.deleteCaseTemplates;
    }

    return {
        account: {
            isAccountAdmin,
            canCreateCases,
            canEditCases,
            canDeleteCases,
            canViewAllCases
        },
        cases: {
            canEditCaseAsAdmin,
            isCaseLead,
            canEditCaseInfo,
            canEditCaseEngagement,
            canViewCaseEngagement,
            canCreateMilestones,
            canEditMilestones,
            canDeleteMilestones,
            canCompleteMilestones,
            canReorderMilestones,
            canReopenMilestones,
            canViewAllTasks,
            canCreateTasks,
            canCreateTasksForOthers,
            canEditTasks,
            canAddEvents,
            canEditEvents,
            canDeleteEvents,
            canAddNotes,
            canViewDocuments,
            canUploadDocuments,
            canDownloadDocuments,
            canEditDocuments,
            canDeleteDocuments
        },
        settings: {
            canAccessAccountSettings,
            canChangeAccountSettings,
            canEditCaseDataTypes,
            canAddEditParties,
            canDeleteParties
        },
        users: {
            canAddEditUsers,
            canDeleteUsers,
        },
        timeTracking: {
            canAddEditTrackedTime,
            canDeleteTrackedTime,
            canViewExpenses,
            canAddExpenses,
            canDeleteOwnExpenses,
            canViewBillableRates,
            canEditBillableRates
        },
        invoices: {
            canViewInvoices,
            canCreateInvoices,
            canDeleteInvoices
        },
        templates: {
            canSeeTemplates,
            canSeeEngagementTemplates,
            canSeeCaseTemplates,
            canCreateEditEngagementTemplates,
            canDeleteEngagementTemplates,
            canCreateEditCaseTemplates,
            canDeleteCaseTemplates
        }
    }
}